/**
 * 冰箱里的一行食材。
 *
 * 交互有两层，都要有：
 *   · 点整行 → 编辑
 *   · 左滑 → 露出"用完了"
 *
 * 左滑不是炫技：冰箱这里最高频的操作就是"这东西没了"，
 * 每行常驻一个删除按钮会把列表塞满按钮，滑动才是系统给出的答案。
 *
 * 结构是 useSwipeReveal 约定的三段式：外层夹住 + 右侧操作区 + 可平移的内容。
 * 操作区宽度必须和 hook 里的 revealWidth 一致（REVEAL_WIDTH）。
 */

import { storedDaysText, type FridgeItem } from '@fridge/shared';

import { useSwipeReveal } from '../motion';
import { GLYPHS, IconGlyph } from './IconGlyph';

const REVEAL_WIDTH = 92;

export interface FridgeRowProps {
	item: FridgeItem;
	onEdit: (item: FridgeItem) => void;
	onConsume: (item: FridgeItem) => void;
}

export function FridgeRow({ item, onEdit, onConsume }: FridgeRowProps): JSX.Element {
	const { rowRef, isOpen } = useSwipeReveal({
		revealWidth: REVEAL_WIDTH,
		onFullSwipe: () => onConsume(item),
	});

	return (
		<li
			ref={rowRef}
			data-flip-id={item.id}
			data-stagger
			className="relative overflow-hidden bg-surface"
		>
			{/* 右侧操作区，被内容盖住，左滑才露出来。
			 * 收起状态必须 aria-hidden + 不可点击，否则读屏会为每一行念一遍"用完了"，
			 * 而且透明度为 0 的按钮仍然能被点到。 */}
			<div
				data-swipe-actions
				aria-hidden={!isOpen}
				className={`absolute inset-y-0 right-0 flex opacity-0 ${
					isOpen ? '' : 'pointer-events-none'
				}`}
				style={{ width: REVEAL_WIDTH }}
			>
				<button
					type="button"
					tabIndex={isOpen ? 0 : -1}
					onClick={() => onConsume(item)}
					className="flex w-full flex-col items-center justify-center gap-1 bg-danger-soft text-danger"
				>
					<IconGlyph glyph={GLYPHS.check} size={18} weight="bold" />
					<span className="text-[11px] font-medium">用完了</span>
				</button>
			</div>

			<button
				type="button"
				data-swipe-content
				onClick={() => onEdit(item)}
				className="relative flex w-full items-center gap-3 bg-surface px-4 py-3 text-left"
				style={{ touchAction: 'pan-y' }}
			>
				<div className="min-w-0 flex-1">
					<div className="flex items-baseline gap-2">
						<span className="truncate text-[16px] text-label">{item.name}</span>
						<span className="tnum shrink-0 text-[13px] text-label-2">
							{formatQty(item.qty)}
							{item.unit}
						</span>
					</div>
					<div className="mt-1.5 flex items-center gap-2 text-[11.5px] text-label-2">
						{/* 只如实报"放了几天"。新不新鲜由人判断，这里不做也不显示任何判定 */}
						<span className="tnum shrink-0">{storedDaysText(item.storedAt)}</span>
						<span className="text-label-3">·</span>
						<span className="truncate">{item.category}</span>
					</div>
				</div>

				<IconGlyph glyph={GLYPHS.next} size={15} className="shrink-0 text-label-3" />
			</button>
		</li>
	);
}

function formatQty(qty: number): string {
	return Number.isInteger(qty) ? String(qty) : qty.toFixed(1).replace(/\.0$/, '');
}
